import axios from "axios";
import {
  SET_PROJECTS,
  SET_LOGS,
  START_LOADING,
  STOP_LOADING,
  RESET_LOGS,
} from "../types";
import { COMPANY_ID, API_VERSION } from "../../util/config";
import { options } from "../../util/localDB";
import { runLimited, MAX_CONCURRENT_REQUESTS } from "../../util/throttle";

const MAX_RECENT_DATE = new Date(new Date().setDate(new Date().getDate() - 30));

// --- Recent-log cache (Sept 2026) -----------------------------------------
// When the dashboard loads it already asks every project for its last 30
// days of delivery logs (to find which projects are active). We keep those
// answers so searches inside the last 30 days can be answered without asking
// Procore again, which avoids hitting Procore's rate limit.
const CACHE_TTL_MS = 10 * 60 * 1000; // re-ask Procore after 10 minutes
// Procore returns results in pages; if a project filled a whole page we
// can't be sure we have everything, so we ask Procore directly instead.
const FULL_PAGE = 100;
const recentLogCache = {}; // projectId -> logs from the last 30 days
let recentLogCacheTime = 0;

// Each new search gets a number; older searches stop when a newer one starts.
let currentSearch = 0;

const formatDate = (date) => {
  return `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}-${(
    "0" + date.getDate()
  ).slice(-2)}`;
};

export const fetchProjects = () => async (dispatch) => {
  dispatch({ type: START_LOADING });
  try {
    let { data: projects } = await axios.get(
      `${API_VERSION}/projects?company_id=${COMPANY_ID}&serializer_view=compact`
    );
    let today = new Date();

    // Check each project for recent logs (paced to stay under Procore's limits)
    await runLimited(projects, MAX_CONCURRENT_REQUESTS, (project) =>
      axios
        .get(
          `${API_VERSION}/projects/${
            project.id
          }/delivery_logs?start_date=${formatDate(
            MAX_RECENT_DATE
          )}&end_date=${formatDate(today)}`
        )
        .then(({ data: logs }) => {
          project["recentlyUpdated"] = logs.length !== 0;
          recentLogCache[project.id] = logs;
        })
        .catch((err) => {
          console.log(err);
        })
    );
    recentLogCacheTime = Date.now();
    dispatch({ type: SET_PROJECTS, payload: projects });
  } catch (err) {
    console.log(err);
  }
};

// Keeps only the logs that match the user's "contents" filter
const filterByContents = (logs, filters) => {
  if (filters.contents.length === 0) return logs;
  return logs.filter((log) => {
    let logContents = log.contents.split(/, [\n,\r]*/g);
    // first, check if any of the filters are included in the log contents
    if (filters.contents.some((c) => logContents.includes(c.value))) {
      return true;
    }
    // next, if no exact matches and user is looking for Other
    else if (filters.contents.some((c) => c.value === "Other")) {
      // anything in the log contents that isn't a predefined content counts as "Other"
      return (
        logContents.filter((c) => !options.some((fc) => fc.value === c))
          .length !== 0
      );
    }
    return false;
  });
};

export const fetchLogs = (projects, filters) => (dispatch) => {
  const searchId = ++currentSearch;
  const stillCurrent = () => searchId === currentSearch;

  dispatch({ type: START_LOADING });
  dispatch({ type: RESET_LOGS });

  const start = formatDate(filters.startDate);
  const end = formatDate(filters.endDate);
  let recentOnly = filters.startDate > MAX_RECENT_DATE;
  const cacheFresh = Date.now() - recentLogCacheTime < CACHE_TTL_MS;

  const showLogs = (project, logs) => {
    if (!stillCurrent()) return; // a newer search has started
    const results = filterByContents(logs, filters);
    if (results.length !== 0) {
      dispatch({ type: SET_LOGS, payload: { project, logs: results } });
    }
  };

  // only look at projects that can have logs in the chosen date range
  const projectsToQuery = projects.filter(
    (project) => !(recentOnly && !project.recentlyUpdated)
  );

  const needProcore = [];
  projectsToQuery.forEach((project) => {
    const cached = recentLogCache[project.id];
    if (recentOnly && cacheFresh && cached && cached.length < FULL_PAGE) {
      // answer from the logs we already have (no Procore request)
      showLogs(
        project,
        cached.filter((log) => log.date >= start && log.date <= end)
      );
    } else {
      needProcore.push(project);
    }
  });

  // Ask Procore for the rest, paced to stay under its rate limits
  runLimited(
    needProcore,
    MAX_CONCURRENT_REQUESTS,
    (project) =>
      axios
        .get(
          `${API_VERSION}/projects/${project.id}/delivery_logs?start_date=${start}&end_date=${end}`
        )
        .then(({ data: logs }) => showLogs(project, logs))
        .catch((err) => console.log(err)),
    stillCurrent
  ).then(() => {
    if (stillCurrent()) dispatch({ type: STOP_LOADING });
  });
};
